<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Plus } from 'lucide-vue-next'
import { normalizeApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import {
  createProduct,
  getProduct,
  listProductGroups,
  listUnitReferences,
  updateProduct,
} from '../productService'
import {
  catalogTypeForKind,
  isManufacturedProduct,
} from '../productPresentation'
import type {
  CatalogKind,
  EngineeringProduct,
  ProductGroupOption,
  ProductPayload,
  UnitReference,
} from '../types'

interface FormErrors {
  name?: string
  sku?: string
  type?: string
  unit?: string
  audience_segment?: string
  season?: string
  min_order_quantity?: string
  socks_per_pack?: string
  packs_per_bag?: string
  group_id?: string
  tags?: string
}

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()

const loading = ref(true)
const submitting = ref(false)
const loadError = ref('')
const submitError = ref('')
const groups = ref<ProductGroupOption[]>([])
const units = ref<UnitReference[]>([])
const formProduct = ref<EngineeringProduct | null>(null)
const errors = reactive<FormErrors>({})
const unitsLoading = ref(false)
const unitsError = ref('')

const productId = computed(() => Number(route.params.id))
const isEditing = computed(() => Number.isInteger(productId.value) && productId.value > 0)
const canManage = computed(() => auth.user?.permissions.includes('products.manage') ?? false)
const catalogKind = computed<CatalogKind>(() =>
  route.meta.catalogKind === 'raw-materials' ||
  route.meta.catalogKind === 'spare-parts'
    ? route.meta.catalogKind
    : 'manufactured',
)
const isManufacturedCatalog = computed(() => catalogKind.value === 'manufactured')
const unitOptions = computed(() => {
  const currentUnit = form.unit.trim()
  if (!currentUnit || units.value.some((unit) => unit.name === currentUnit)) {
    return units.value
  }

  return [
    {
      id: -1,
      type: 'unit',
      code: currentUnit.toUpperCase(),
      name: currentUnit,
      base_amount: null,
      is_active: true,
      sort_order: -1,
    } satisfies UnitReference,
    ...units.value,
  ]
})
const listPath = computed(() =>
  catalogKind.value === 'manufactured' ? '/products' : `/${catalogKind.value}`,
)
const detailPath = computed(() =>
  isEditing.value
    ? (catalogKind.value === 'manufactured'
      ? `/products/${productId.value}`
      : `/${catalogKind.value}/${productId.value}`)
    : listPath.value,
)
const catalogType = computed(() =>
  catalogKind.value === 'manufactured'
    ? form.type
    : (catalogTypeForKind(catalogKind.value) as ProductPayload['type'] | undefined) ?? form.type,
)
const pageTitleKey = computed(() =>
  catalogKind.value === 'raw-materials'
    ? 'raw'
    : catalogKind.value === 'spare-parts'
      ? 'spare'
      : 'manufactured',
)

const form = reactive({
  name: '',
  sku: '',
  internal_sku_code: '',
  type: 'manufactured_good' as ProductPayload['type'],
  audience_segment: '' as '' | NonNullable<ProductPayload['audience_segment']>,
  season: '' as '' | NonNullable<ProductPayload['season']>,
  unit: 'pair',
  ean13: '',
  min_order_quantity: '',
  socks_per_pack: '',
  packs_per_bag: '',
  group_id: '',
  tags: '',
})

function clearErrors(): void {
  for (const key of Object.keys(errors) as Array<keyof FormErrors>) {
    delete errors[key]
  }
  submitError.value = ''
}

function assignForm(product: EngineeringProduct): void {
  form.name = product.name
  form.sku = product.sku
  form.internal_sku_code = product.internal_sku_code ?? ''
  form.type = isManufacturedProduct(product)
    ? product.type as ProductPayload['type']
    : 'manufactured_good'
  form.audience_segment = (product.audience_segment as typeof form.audience_segment) ?? ''
  form.season = (product.season as typeof form.season) ?? ''
  form.unit = product.unit
  form.ean13 = product.ean13 ?? ''
  form.min_order_quantity = product.min_order_quantity != null ? String(product.min_order_quantity) : ''
  form.socks_per_pack = product.socks_per_pack != null ? String(product.socks_per_pack) : ''
  form.packs_per_bag = product.packs_per_bag != null ? String(product.packs_per_bag) : ''
  form.group_id = product.group_id != null ? String(product.group_id) : ''
  form.tags = product.tags.join(', ')
}

function positiveInteger(value: string): boolean {
  return Number.isInteger(Number(value)) && Number(value) > 0
}

function validate(): boolean {
  clearErrors()

  if (!form.name.trim()) errors.name = t('engineering.productForm.validation.name')
  if (!form.sku.trim()) errors.sku = t('engineering.productForm.validation.sku')
  if (!form.type) errors.type = t('engineering.productForm.validation.type')
  if (!form.unit.trim()) errors.unit = t('engineering.productForm.validation.unit')
  if (form.min_order_quantity && !positiveInteger(form.min_order_quantity)) errors.min_order_quantity = t('engineering.productForm.validation.minOrder')
  if (form.socks_per_pack && !positiveInteger(form.socks_per_pack)) errors.socks_per_pack = t('engineering.productForm.validation.socksPerPack')
  if (form.packs_per_bag && !positiveInteger(form.packs_per_bag)) errors.packs_per_bag = t('engineering.productForm.validation.packsPerBag')

  return Object.keys(errors).length === 0
}

function buildPayload(): ProductPayload {
  return {
    name: form.name.trim(),
    sku: form.sku.trim(),
    internal_sku_code: form.internal_sku_code.trim() || undefined,
    type: catalogType.value,
    audience_segment: isManufacturedCatalog.value ? form.audience_segment || undefined : undefined,
    season: isManufacturedCatalog.value ? form.season || undefined : undefined,
    unit: form.unit.trim(),
    ean13: form.ean13.trim() || undefined,
    min_order_quantity: form.min_order_quantity ? Number(form.min_order_quantity) : undefined,
    socks_per_pack: form.socks_per_pack ? Number(form.socks_per_pack) : undefined,
    packs_per_bag: form.packs_per_bag ? Number(form.packs_per_bag) : undefined,
    group_id: form.group_id ? Number(form.group_id) : undefined,
    tags: form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
  }
}

async function loadDependencies(): Promise<void> {
  loading.value = true
  loadError.value = ''

  try {
    const [groupList, product] = await Promise.all([
      listProductGroups(client),
      isEditing.value ? getProduct(client, productId.value) : Promise.resolve(null),
    ])
    groups.value = groupList

    if (product) {
      const matchesSection = catalogKind.value === 'manufactured'
        ? isManufacturedProduct(product)
        : product.type === catalogTypeForKind(catalogKind.value)
      if (!matchesSection) {
        throw new Error(t('engineering.productForm.invalidType'))
      }
      formProduct.value = product
      assignForm(product)
    } else if (!isManufacturedCatalog.value) {
      form.type = 'manufactured_good'
    }
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    loadError.value = normalized.message
    return
  } finally {
    loading.value = false
  }

  await reloadUnits()
}

async function reloadUnits(): Promise<void> {
  unitsLoading.value = true
  unitsError.value = ''

  try {
    units.value = await listUnitReferences(client)
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    unitsError.value = normalized.message
  } finally {
    unitsLoading.value = false
  }
}

async function submit(): Promise<void> {
  if (!validate()) return

  submitting.value = true
  try {
    const payload = buildPayload()
    const product = isEditing.value
      ? await updateProduct(client, productId.value, payload)
      : await createProduct(client, payload)
    await router.push(`/products/${product.id}`)
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    submitError.value = normalized.correlationId
      ? `${normalized.message} Reference: ${normalized.correlationId}`
      : normalized.message

    if (normalized.errors) {
      for (const [field, messages] of Object.entries(normalized.errors)) {
        const key = field as keyof FormErrors
        if (messages[0] && key in errors) {
          errors[key] = messages[0]
        }
      }
    }
  } finally {
    submitting.value = false
  }
}

onMounted(loadDependencies)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :to="isEditing ? detailPath : listPath">
      {{ isEditing ? $t('engineering.productForm.backItem') : $t('engineering.productForm.backList') }}
    </RouterLink>
    <div v-if="canManage" class="page-toolbar__actions">
      <button class="page-toolbar__action--primary" type="submit" form="product-form" :disabled="loading || submitting">
        <Plus v-if="!submitting" :size="16" aria-hidden="true" />
        {{ submitting ? $t('engineering.productForm.saving') : isEditing ? $t('engineering.productForm.save') : $t('engineering.productForm.create') }}
      </button>
    </div>
  </div>

  <div class="page-heading">
    <p class="eyebrow">{{ $t('engineering.productForm.eyebrow') }}</p>
    <h2>{{ isEditing ? $t(`engineering.productForm.editTitle.${pageTitleKey}`) : $t(`engineering.productForm.createTitle.${pageTitleKey}`) }}</h2>
    <p>
      {{ isEditing
        ? $t(`engineering.productForm.editDescription.${pageTitleKey}`)
        : $t(`engineering.productForm.createDescription.${pageTitleKey}`) }}
    </p>
  </div>

  <div v-if="loading" class="form-state">{{ $t('engineering.productForm.loading') }}</div>
  <div v-else-if="loadError" class="form-state form-state--error">
    <p role="alert">{{ loadError }}</p>
    <button type="button" @click="loadDependencies">{{ $t('engineering.action.retry') }}</button>
  </div>

  <form v-else id="product-form" @submit.prevent="submit">
    <div v-if="submitError" class="error-banner" role="alert">{{ submitError }}</div>

    <section>
      <h3>{{ $t('engineering.productForm.identity') }}</h3>
      <div class="form-grid">
        <label>
          <span>{{ $t('engineering.productForm.name') }} *</span>
          <input v-model="form.name" type="text" :aria-invalid="Boolean(errors.name)">
          <small v-if="errors.name">{{ errors.name }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.sku') }} *</span>
          <input v-model="form.sku" type="text" :aria-invalid="Boolean(errors.sku)">
          <small v-if="errors.sku">{{ errors.sku }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.internalSku') }}</span>
          <input v-model="form.internal_sku_code" type="text">
        </label>
        <label>
          <span>{{ $t('engineering.productForm.barcode') }}</span>
          <input v-model="form.ean13" type="text" inputmode="numeric">
        </label>
      </div>
    </section>

    <section>
      <h3>{{ $t('engineering.productForm.classification') }}</h3>
      <div class="form-grid">
        <label v-if="isManufacturedCatalog">
          <span>{{ $t('engineering.productForm.type') }} *</span>
          <select v-model="form.type" :aria-invalid="Boolean(errors.type)">
            <option value="manufactured_good">{{ $t('engineering.catalog.manufacturedGood') }}</option>
            <option value="finished_good">{{ $t('engineering.catalog.finishedGood') }}</option>
            <option value="semi_finished_good">{{ $t('engineering.catalog.semiFinishedGood') }}</option>
            <option value="semi_finished">{{ $t('engineering.catalog.semiFinished') }}</option>
          </select>
          <small v-if="errors.type">{{ errors.type }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.group') }}</span>
          <select v-model="form.group_id" :aria-invalid="Boolean(errors.group_id)">
            <option value="">{{ $t('engineering.productForm.selectGroup') }}</option>
            <option v-for="group in groups" :key="group.id" :value="String(group.id)">
              {{ group.name }} ({{ group.code }})
            </option>
          </select>
          <small v-if="errors.group_id">{{ errors.group_id }}</small>
        </label>
        <label v-if="isManufacturedCatalog">
          <span>{{ $t('engineering.productForm.audience') }}</span>
          <select v-model="form.audience_segment" :aria-invalid="Boolean(errors.audience_segment)">
            <option value="">{{ $t('engineering.product.notSet') }}</option>
            <option value="kids">{{ $t('engineering.catalog.kids') }}</option>
            <option value="teens">{{ $t('engineering.catalog.teens') }}</option>
            <option value="men">{{ $t('engineering.catalog.men') }}</option>
            <option value="women">{{ $t('engineering.catalog.women') }}</option>
          </select>
          <small v-if="errors.audience_segment">{{ errors.audience_segment }}</small>
        </label>
        <label v-if="isManufacturedCatalog">
          <span>{{ $t('engineering.productForm.season') }}</span>
          <select v-model="form.season" :aria-invalid="Boolean(errors.season)">
            <option value="">{{ $t('engineering.product.notSet') }}</option>
            <option value="winter">{{ $t('engineering.catalog.winter') }}</option>
            <option value="summer">{{ $t('engineering.catalog.summer') }}</option>
            <option value="all_season">{{ $t('engineering.catalog.allSeason') }}</option>
          </select>
          <small v-if="errors.season">{{ errors.season }}</small>
        </label>
      </div>
    </section>

    <section>
      <h3>{{ $t('engineering.productForm.packaging') }}</h3>
      <div class="form-grid" :class="{ 'form-grid--compact': !isManufacturedCatalog }">
        <label>
          <span>{{ $t('engineering.productForm.unit') }} *</span>
          <select v-model="form.unit" :aria-invalid="Boolean(errors.unit)" :disabled="unitsLoading || !unitOptions.length">
            <option value="">{{ unitsLoading ? $t('engineering.productForm.unitsLoading') : $t('engineering.productForm.selectUnit') }}</option>
            <option v-for="unit in unitOptions" :key="unit.id" :value="unit.name">
              {{ unit.name }} ({{ unit.code }})
            </option>
          </select>
          <small v-if="errors.unit">{{ errors.unit }}</small>
          <small v-else-if="unitsError">
            {{ unitsError }}
            <button class="inline-retry" type="button" @click="reloadUnits">{{ $t('engineering.action.retry') }}</button>
          </small>
          <small v-else>{{ $t('engineering.productForm.unitHelp') }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.minOrder') }}</span>
          <input v-model="form.min_order_quantity" type="number" min="1" step="1" :aria-invalid="Boolean(errors.min_order_quantity)">
          <small v-if="errors.min_order_quantity">{{ errors.min_order_quantity }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.socksPerPack') }}</span>
          <input v-model="form.socks_per_pack" type="number" min="1" step="1" :aria-invalid="Boolean(errors.socks_per_pack)">
          <small v-if="errors.socks_per_pack">{{ errors.socks_per_pack }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.packsPerBag') }}</span>
          <input v-model="form.packs_per_bag" type="number" min="1" step="1" :aria-invalid="Boolean(errors.packs_per_bag)">
          <small v-if="errors.packs_per_bag">{{ errors.packs_per_bag }}</small>
        </label>
      </div>
    </section>

    <section>
      <h3>{{ $t('engineering.productForm.tagsSection') }}</h3>
      <label>
        <span>{{ $t('engineering.productForm.tags') }}</span>
        <input v-model="form.tags" type="text" :placeholder="$t('engineering.productForm.tagsPlaceholder')" :aria-invalid="Boolean(errors.tags)">
        <small v-if="errors.tags">{{ errors.tags }}</small>
        <small v-else>{{ $t('engineering.productForm.tagsHelp') }}</small>
      </label>
    </section>
  </form>
</template>

<style scoped>
.back-link{display:inline-block;margin-bottom:20px;color:#285b9a;font-size:13px;font-weight:700}.page-heading{margin-bottom:24px}.eyebrow{margin:0 0 5px;color:#65736e;font-size:12px;font-weight:700;text-transform:uppercase}h2{margin:0;font-size:26px;letter-spacing:0}.page-heading>p:last-child{margin:7px 0 0;color:#64716c}section{margin-top:16px;padding:22px;border:1px solid #d7dfdc;background:#ffffff}section h3{margin:0 0 18px;font-size:16px;letter-spacing:0}.form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px}.form-grid--compact label:last-child:nth-child(4){grid-column:2}.form-grid--compact label:last-child:nth-child(2){grid-column:auto}label>span{display:block;margin-bottom:6px;color:#4e5d57;font-size:12px;font-weight:700}input,select{width:100%;min-height:42px;padding:9px 10px;border:1px solid #adb9b4;border-radius:4px;color:#17201d;background:#ffffff}input[aria-invalid="true"],select[aria-invalid="true"]{border-color:#b44839}label small{display:block;margin-top:5px;color:#697671;font-size:12px}label [aria-invalid="true"]+small{color:#9b3427}.error-banner,.form-state--error{border:1px solid #d7a8a1;color:#852d22;background:#fff5f3}.error-banner{padding:12px 14px}.form-state{padding:32px;border:1px dashed #c7d0cc;color:#65726d;background:#ffffff;text-align:center}.form-state button{min-height:38px;padding:7px 12px;border:1px solid #87958f;border-radius:4px;background:#ffffff;cursor:pointer}@media(max-width:700px){.form-grid{grid-template-columns:1fr}.form-grid--compact label:last-child:nth-child(4){grid-column:auto}section{padding:18px 14px}}
</style>
